import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Editor, posToDOMRect } from '@tiptap/core'
import { setSnackbar } from 'utils/common/actions'
import { buildExtensions } from './extensions'
import { normalizeLegacyHtml } from './legacyHtml'
import { uploadFile } from './api'
import { nodeForFile } from './mediaNodes'
import SuggestionMenu from './SuggestionMenu'
import MediaPicker, { insertBlockAt } from './MediaPicker'
import BubbleMenu from './BubbleMenu'
import TableMenu from './TableMenu'
import BlockHandle from './BlockHandle'
import AiPrompt from './AiPrompt'
import AiChatPanel from './AiChatPanel'
import { promptRange } from './aiContent'
import useStyles from './styles'


const getEditorHtml = editor => (editor.isEmpty ? '' : editor.getHTML())

const emptySuggestion = { kind: null, suggestion: null, index: 0 }

const RichTextField = (props) => {
  const {
    setValue,
    defaultValue,
    error,
    aiChatKey
  } = props
  const localClasses = useStyles()
  const dispatch = useDispatch()
  const containerRef = useRef(null)
  const mountRef = useRef(null)
  const inputTimeout = useRef(0)
  const lastDefaultValue = useRef(defaultValue || '')
  const setValueRef = useRef(setValue)
  setValueRef.current = setValue
  const [editor, setEditor] = useState(null)
  const [menu, setMenu] = useState(emptySuggestion)
  const menuRef = useRef(menu)
  const [media, setMedia] = useState(null)
  const [uploadCount, setUploadCount] = useState(0)
  const [aiPrompt, setAiPrompt] = useState(null)
  const [aiChatOpen, setAiChatOpen] = useState(false)

  const updateMenu = (next) => {
    menuRef.current = next
    setMenu(next)
  }

  useEffect(() => {
    const emitValue = (instance) => {
      clearTimeout(inputTimeout.current)
      inputTimeout.current = 0
      setValueRef.current(getEditorHtml(instance))
    }

    const handleSuggestionChange = (kind, suggestion) => {
      const current = menuRef.current
      if (!suggestion) {
        // Only the menu that is open may close itself
        if (current.kind === kind) {
          updateMenu(emptySuggestion)
        }
        return
      }
      const sameKind = current.kind === kind && current.suggestion
      if (suggestion.loading && sameKind) {
        // Keep showing the previous results until the new ones arrive
        // rather than flashing an empty menu on every keystroke
        updateMenu({ ...current, suggestion: { ...suggestion, items: current.suggestion.items } })
        return
      }
      const sameQuery = sameKind && current.suggestion.query === suggestion.query
      updateMenu({ kind, suggestion, index: sameQuery ? Math.min(current.index, suggestion.items.length - 1) : 0 })
    }

    const handleSuggestionItems = (kind, query, items) => {
      const current = menuRef.current
      // Late results only belong to the menu still showing their query
      if (current.kind !== kind || !current.suggestion || current.suggestion.loading ||
          current.suggestion.query !== query) {
        return
      }
      updateMenu({ ...current, suggestion: { ...current.suggestion, items, pagesPending: false } })
    }

    const handleSuggestionKeyDown = (event) => {
      const { suggestion, index } = menuRef.current
      if (!suggestion || !suggestion.items.length) {
        return false
      }
      const count = suggestion.items.length
      if (event.key === 'ArrowDown') {
        updateMenu({ ...menuRef.current, index: (index + 1) % count })
        return true
      }
      if (event.key === 'ArrowUp') {
        updateMenu({ ...menuRef.current, index: (index + count - 1) % count })
        return true
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        suggestion.command(suggestion.items[Math.max(index, 0)])
        return true
      }
      return false
    }

    let instance = null

    const handleOpenMedia = (type) => {
      const { from, to } = instance.state.selection
      setMedia({ type, position: from, rect: posToDOMRect(instance.view, from, to) })
    }

    const handleOpenAi = () => {
      setAiPrompt({ key: Date.now(), range: promptRange(instance.state) })
    }

    const handleFiles = (files, position) => {
      files.forEach((file) => {
        setUploadCount(count => count + 1)
        uploadFile(file)
          .then(uploaded => insertBlockAt(instance, position, nodeForFile({ ...uploaded, type: file.type })))
          .catch(() => dispatch(setSnackbar(`Could not upload ${file.name}`, 'error')))
          .then(() => setUploadCount(count => count - 1))
      })
    }

    instance = new Editor({
      element: mountRef.current,
      extensions: buildExtensions({
        onSuggestionChange: handleSuggestionChange,
        onSuggestionItems: handleSuggestionItems,
        onSuggestionKeyDown: handleSuggestionKeyDown,
        onOpenMedia: handleOpenMedia,
        onOpenAi: handleOpenAi,
        onFiles: handleFiles
      }),
      content: normalizeLegacyHtml(defaultValue),
      editorProps: {
        attributes: { class: 'rich-text-content' }
      },
      onUpdate: ({ editor: updated }) => {
        clearTimeout(inputTimeout.current)
        inputTimeout.current = setTimeout(() => emitValue(updated), 300)
      },
      onBlur: ({ editor: blurred }) => {
        if (inputTimeout.current) {
          emitValue(blurred)
        }
      }
    })
    setEditor(instance)

    return () => {
      clearTimeout(inputTimeout.current)
      instance.destroy()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load new content when the parent swaps the edited record
  useEffect(() => {
    const incoming = defaultValue || ''
    if (!editor || incoming === lastDefaultValue.current) {
      return
    }
    lastDefaultValue.current = incoming
    editor.commands.setContent(normalizeLegacyHtml(incoming), { emitUpdate: false })
  }, [editor, defaultValue])

  const closeMedia = useCallback(() => setMedia(null), [])
  const closeAiPrompt = useCallback(() => setAiPrompt(null), [])
  const openAiPrompt = useCallback(() => {
    setAiPrompt({ key: Date.now(), range: promptRange(editor.state) })
  }, [editor])

  return (
    <div
      ref={containerRef}
      className={`${localClasses.editor} ${error ? 'error' : ''}`}
    >
      <div ref={mountRef} />
      {/* Sticks to the viewport bottom while the editor's end is scrolled out of view */}
      <div className={localClasses.footer}>
        <button
          type='button'
          className={localClasses.aiButton}
          data-active={aiChatOpen}
          onMouseDown={event => event.preventDefault()}
          onClick={() => setAiChatOpen(!aiChatOpen)}
          title='Chat with the AI assistant about this document'
        >
          ✦ AI
        </button>
        {
          uploadCount > 0 &&
          <div className={localClasses.uploadStatus}>
            Uploading {uploadCount} {uploadCount === 1 ? 'file' : 'files'}…
          </div>
        }
      </div>
      {
        editor &&
        <>
          <BlockHandle editor={editor} containerRef={containerRef} />
          <BubbleMenu editor={editor} onAskAi={openAiPrompt} />
          <TableMenu editor={editor} />
          <SuggestionMenu
            editor={editor}
            kind={menu.kind}
            suggestion={menu.suggestion}
            selectedIndex={menu.index}
            setSelectedIndex={index => updateMenu({ ...menuRef.current, index })}
          />
          {
            media &&
            <MediaPicker editor={editor} media={media} onClose={closeMedia} />
          }
          {
            aiPrompt &&
            <AiPrompt key={aiPrompt.key} editor={editor} range={aiPrompt.range} onClose={closeAiPrompt} />
          }
          {
            aiChatOpen &&
            <AiChatPanel editor={editor} chatKey={aiChatKey} onClose={() => setAiChatOpen(false)} />
          }
        </>
      }
    </div>
  )
}

export default RichTextField
